import { isDefined } from '@library/guards'
import type { AttributeBoundary, AttributeType } from '@library/types'
import {
  amplitudePalette,
  apparentPolarityPalette,
  frequencyHighPalette,
  FxAlternativePalette,
  instPhaseSharpPalette,
  RMSPalette,
  semblancePalette,
} from 'common/constants'
import type { Nullable } from 'common/types'
import type { Axis, NumberValue, ScaleLinear, Selection } from 'd3'
import { axisBottom, axisLeft, axisRight, axisTop, select } from 'd3'

import {
  BOTTOM_TICK_OFFSET_Y,
  END_GRADIENT_OFFSET,
  GRADIENT_OFFSET,
  START_GRADIENT_OFFSET,
  TICK_OFFSET_X,
  TICK_ROTATION,
  TOP_TICK_OFFSET_Y,
} from './constants'
import type { Position } from './types'

const getTicksCount = (boundary: AttributeBoundary, step?: number, tickCount?: number) => {
  if (boundary.start === boundary.stop) {
    return 1
  }

  if (tickCount) {
    return tickCount
  }

  return step ? Math.floor(Math.abs(boundary.stop - boundary.start) / step + 1) : 10
}

const getMaxTickLabelWidth = (texts: SVGTextElement[]) => {
  return texts.reduce<number>(
    (length, text) => Math.max(length, text.getBoundingClientRect().width),
    0,
  )
}

export const getTicks = (
  boundary: AttributeBoundary,
  attribute: AttributeType,
  scaleRange: number,
  scale: ScaleLinear<number, number>,
  fontSize: number,
  step?: number,
  tickCount?: number,
) => {
  const rangeStop = scaleRange - END_GRADIENT_OFFSET - 3
  const count = getTicksCount(boundary, step, tickCount)
  const initialTicks = []

  if (count > 1) {
    const rangeStep = rangeStop / count
    for (let i = 0; i <= rangeStop; i += rangeStep) {
      initialTicks.push(scale.invert(i))
    }
  } else {
    initialTicks.push(scale.invert(rangeStop))
  }

  const expectedlabelWidth =
    Math.max(...initialTicks.map(tick => tickFormatter(tick, attribute).length)) * fontSize

  if (!step && !tickCount) {
    return getUnoverlappedTicks(initialTicks, expectedlabelWidth, scaleRange)
  }

  return initialTicks
}

const getUnoverlappedTicks = (
  ticks: number[],
  expectedlabelWidth: number,
  availableWidth: number,
) => {
  const maxNumberOfTicks = Math.floor(availableWidth / expectedlabelWidth)

  return ticks.filter((_, i) => i % Math.ceil(ticks.length / maxNumberOfTicks) === 0)
}

const tickFormatter = (value: number, attribute?: AttributeType): string =>
  attribute === 'semblance' ? value.toFixed(1) : value.toFixed(2)

const translate = (right: number, top: number): string => `translate(${right}, ${top})`
const rotation = (angle: number): string => `rotate(${angle})`

export const getAxisCreateFunc = (
  scale: ScaleLinear<number, number>,
  tickValues: number[],
  attribute: AttributeType,
  isHorizontal: boolean,
  axisPosition?: Position,
) => {
  let axis: Axis<NumberValue>

  switch (axisPosition) {
    case 'top':
      axis = axisTop(scale)
      break
    case 'bottom':
      axis = axisBottom(scale)
      break
    case 'right':
      axis = axisRight(scale)
      break
    case 'left':
      axis = axisLeft(scale)
      break
    default:
      if (isHorizontal) {
        axis = axisTop(scale)
      } else {
        axis = axisRight(scale)
      }
  }

  return axis
    .tickValues(tickValues)
    .tickSizeOuter(0)
    .tickFormat(value => tickFormatter(value.valueOf(), attribute))
}

export const updateTickLabel = (
  svgElement: SVGSVGElement,
  width: number,
  isHorizontal: boolean,
  axisPosition?: Position,
) => {
  const elements = select(svgElement).selectAll('text')
  const texts = Array.from(elements).map(e => e as SVGTextElement)
  const maxTickLabelWidth = getMaxTickLabelWidth(texts)
  switch (axisPosition) {
    case 'bottom':
      if (maxTickLabelWidth * texts.length > width) {
        elements
          .attr(
            'transform',
            rotation(TICK_ROTATION) + translate(-TICK_OFFSET_X, BOTTOM_TICK_OFFSET_Y),
          )
          .style('text-anchor', 'middle')
      }
      break
    case 'top':
      if (maxTickLabelWidth * texts.length > width) {
        elements
          .attr('transform', rotation(TICK_ROTATION) + translate(TICK_OFFSET_X, TOP_TICK_OFFSET_Y))
          .style('text-anchor', 'middle')
      }
      break
    case 'left':
      elements.attr('text-anchor', 'start').attr('transform', translate(-maxTickLabelWidth, 0))
      break
    case 'right':
      elements.attr('text-anchor', 'end').attr('transform', translate(maxTickLabelWidth, 0))
      break
    case undefined:
      if (isHorizontal && maxTickLabelWidth * texts.length > width) {
        elements
          .attr('transform', rotation(TICK_ROTATION) + translate(TICK_OFFSET_X, TOP_TICK_OFFSET_Y))
          .style('text-anchor', 'middle')
      }
      if (!isHorizontal) {
        elements.attr('text-anchor', 'end').attr('transform', translate(maxTickLabelWidth, 0))
      }
      break
  }
}

export const getTransformAxis = (
  isHorizontal: boolean,
  axisHeight: number,
  axisWidth: number,
  axisPosition?: Position,
) => {
  switch (axisPosition) {
    case 'left':
      return translate(axisWidth, START_GRADIENT_OFFSET)
    case 'bottom':
      return translate(START_GRADIENT_OFFSET, axisHeight)
    default:
      if (isHorizontal) {
        return translate(START_GRADIENT_OFFSET, axisHeight)
      }
      return translate(0, START_GRADIENT_OFFSET)
  }
}

export const createGradientRect = (
  container: Selection<Nullable<SVGGElement>, unknown, null, undefined>,
  id: string,
  width: number,
  height: number,
  isHorizontal: boolean,
  entityId?: Nullable<string>,
) => {
  const rect = container
    .append('rect')

    .attr('x', isHorizontal ? START_GRADIENT_OFFSET : 0)
    .attr('y', isHorizontal ? 0 : START_GRADIENT_OFFSET)
    .attr('rx', 4)
    .attr('ry', 4)

  if (isHorizontal) {
    rect.attr('width', width - GRADIENT_OFFSET).attr('height', height)
  } else {
    rect.attr('width', width).attr('height', height - GRADIENT_OFFSET)
  }

  if (isDefined(entityId)) {
    rect.attr('fill', `url(#${id})`)
  } else {
    rect.attr('fill', 'var(--color-control-bg-ghost-hover)')
  }

  return rect
}

export function isPaletteReversed(attribute: AttributeType | undefined) {
  return attribute === 'fx'
}

export const getPalette = (attribute: AttributeType | undefined) => {
  switch (attribute) {
    case 'rms':
    case 'twt':
    case undefined:
      return RMSPalette
    case 'amplitude':
    case 'envelope':
      return amplitudePalette
    case 'semblance':
      return semblancePalette
    case 'apparentPolarity':
      return apparentPolarityPalette
    case 'inst_phase':
      return instPhaseSharpPalette
    case 'inst_freq':
    case 'dominant_freq':
    case 'inst_bandwidth':
      return frequencyHighPalette
    case 'fx':
      return FxAlternativePalette
    default:
      return undefined
  }
}

export const createGradientDefs = (
  container: Selection<Nullable<SVGGElement>, unknown, null, undefined>,
  id: string,
  palette: number[][],
  isHorizontal: boolean,
) => {
  const defs = container.append('defs')
  const linearGradient = defs.append('linearGradient').attr('id', id)

  if (!isHorizontal) {
    linearGradient.attr('y1', '100%').attr('x2', '0%')
  }

  palette.forEach((color, index) => {
    const cssColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`
    linearGradient
      .append('stop')
      .attr('offset', index / (palette.length - 1))
      .attr('stop-color', cssColor)
  })

  return defs
}
